import { TemplateType } from 'constants/templateTypes';
import { ImageHeader, TextHeader, WhatsAppContentDefinition } from 'modules/graphql';
import { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { Link } from 'ui/links';
import { borderColors, spacing } from 'ui/theme';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { MessageTypeSelect, SettingsSectionHeader } from '../common';
import { displayContent } from '../common/content';
import { DataFeedsFormSection } from '../common/DataFeedsFormSection';
import { TemplateNameField } from '../common/TemplateNameField';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { DOC_INFO_BANNER_DISPLAY_STORAGE_KEY, META_TEMPLATE_DOCUMENTATION_LINK } from './constants';
import { Buttons } from './formFields/Buttons';
import { FooterField } from './formFields/FooterField';
import { HeaderField } from './formFields/HeaderField';
import { ImageHeaderField } from './formFields/ImageHeaderField';
import { MessageTextArea } from './formFields/MessageTextArea';
import { WhatsAppCategory } from './formFields/WhatsAppCategory';
import { WhatsAppSenderSelect } from './formFields/WhatsAppSenderSelect';
import { useEditableWhatsApp } from './hooks/useEditableWhatsApp';
import { BusinessInititatedField, UserInititatedField } from './TemplateTypeField';
import { ShortenAndTrackLinksFormSection } from './formFields/ShortenAndTrackLinksFormSection';
import { GoogleAnalyticsFormSection } from '../common/GoogleAnalyticsFormSection';
import { LinkParametersFormSection } from '../common/LinkParametersFormSection';
import { useWhatsAppLinkShortening } from './context/WhatsAppLinkShorteningContext';

const StyledInfoBanner = styled(InfoBanner)`
  margin-top: ${spacing.base};
`;

const StyledLink = styled(Link)`
  color: inherit;
  text-decoration-color: ${borderColors.borderInfoSubtle};
`;

type Props = PropsWithChildren<{ isReviewTemplateTakeover?: boolean }>;

export const SettingsForm = ({ isReviewTemplateTakeover = false }: Props) => {
  const { campaignId } = useQueryParamContext();
  const { initialTemplate, isEditMode, liveTemplate } = useEditableFieldsContext();
  const [documentationInfoHasBeenSeen, setDocumentationInfoHasBeenSeen] = useLocalStorage<boolean>(
    DOC_INFO_BANNER_DISPLAY_STORAGE_KEY,
    false,
  );
  const { isEditableWhatsAppTemplate: isEditable } = useEditableWhatsApp();

  const isCampaignFlow = !!campaignId;
  const whatsAppData = (initialTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const { templateType } = initialTemplate;

  // only show headers if editable or if not editable, if the headers are present
  const showBusinessHeader =
    isEditable ||
    (!isEditable &&
      ((whatsAppData.header as TextHeader)?.text ||
        (whatsAppData.header as ImageHeader)?.mediaUrl));

  /** If in WhatsApp template review takeover, form should hide field when no data feeds are selected. */
  const shouldShowDataFeedsField = !(isReviewTemplateTakeover && !liveTemplate?.dataFeedIds.length);

  const { isWhatsAppLinkShorteningEnabled } = useWhatsAppLinkShortening();

  return (
    <>
      {isEditMode && !documentationInfoHasBeenSeen && (
        <StyledInfoBanner onClose={() => setDocumentationInfoHasBeenSeen(true)}>
          To learn more about WhatsApp business-initiated best practices and guidelines, review{' '}
          <StyledLink
            hasOpenNewTabIcon
            href={META_TEMPLATE_DOCUMENTATION_LINK}
            rel="noopener noreferrer"
            target="_blank"
          >
            Meta&apos;s documentation
          </StyledLink>
        </StyledInfoBanner>
      )}
      {isCampaignFlow && templateType === TemplateType.UserInitiated && (
        <>
          <SettingsSectionHeader>{displayContent.templateInformation}</SettingsSectionHeader>
          <TemplateNameField />
        </>
      )}

      <SettingsSectionHeader data-test="sending-info-section-header">
        {displayContent.headerSendingInformation}
      </SettingsSectionHeader>

      <BusinessInititatedField>
        <WhatsAppCategory />
      </BusinessInititatedField>

      <MessageTypeSelect isEditable={isEditable} />
      <WhatsAppSenderSelect />

      <SettingsSectionHeader data-test="content-section-header">
        {displayContent.headerContent}
      </SettingsSectionHeader>

      {showBusinessHeader && (
        <BusinessInititatedField>
          <HeaderField isReviewTemplateTakeover={isReviewTemplateTakeover} />
        </BusinessInititatedField>
      )}

      <UserInititatedField>
        <ImageHeaderField />
      </UserInititatedField>

      <MessageTextArea />

      <BusinessInititatedField>
        <FooterField isReviewTemplateTakeover={isReviewTemplateTakeover} />
      </BusinessInititatedField>

      <BusinessInititatedField>
        <Buttons isReviewTemplateTakeover={isReviewTemplateTakeover} />
      </BusinessInititatedField>

      {shouldShowDataFeedsField && <DataFeedsFormSection isEditable={isEditable} />}

      <SettingsSectionHeader data-test="links-section-header">
        {displayContent.headerLinks}
      </SettingsSectionHeader>
      <ShortenAndTrackLinksFormSection />
      <GoogleAnalyticsFormSection
        isLinkShorteningEnabled={isWhatsAppLinkShorteningEnabled}
        isEditDisabled={!isEditable}
      />
      <LinkParametersFormSection
        isLinkShorteningEnabled={isWhatsAppLinkShorteningEnabled}
        isEditDisabled={!isEditable}
      />
    </>
  );
};
